// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

// Chat, reaction và cờ ghi hình cục bộ trong phòng, qua KÊNH DỮ LIỆU của LiveKit.
//
// # Vì sao ở media plane chứ không ở kênh sự kiện của Z-Meet
//
// ADR-0006 §10 đã để ngỏ lựa chọn này. Lý do chọn kênh dữ liệu:
//
//  1. **Nó đi thẳng giữa những người trong phòng, qua SFU.** Không vòng qua
//     control plane, nên độ trễ là độ trễ của media chứ không phải của một
//     round-trip HTTP cộng một lần đọc database.
//  2. **Kênh sự kiện của Z-Meet là MỘT CHIỀU** (server → client). Chat cần
//     chiều ngược lại, và thêm chiều đó là thiết kế lại nó.
//  3. **Chỉ người TRONG phòng nói được.** Quyền phát dữ liệu nằm trong chính
//     token LiveKit (`CanPublishData`), nên người ở phòng chờ — vốn không có
//     token — không gửi được gì. Bất biến số 2 tự bảo vệ chỗ này.
//
// # Cái giá, nói thẳng
//
// **Không có lịch sử.** Người vào sau không thấy tin nhắn trước đó, và đóng tab
// là mất hết. Lưu lịch sử đòi hỏi một bảng, một chính sách lưu trữ, và một
// quyết định pháp lý về việc chat có nằm trong phạm vi ghi hình hay không — ba
// thứ chưa có ADR nào trả lời. Cho tới lúc đó, chat ở đây là **phù du có chủ
// đích**, không phải một chỗ thiếu sót đang chờ ai đó vá.
//
// # Cờ ghi hình cục bộ, và giới hạn của nó
//
// Ai trong phòng cũng ghi hình về máy mình được (recorder.ts), và khi ghi thì
// client tự giương một cờ trên kênh này để mọi người nhìn thấy.
//
// ⚠️ **Đó là một LỜI KHAI, không phải một cơ chế cưỡng chế.** Một người sửa
// client — hoặc chỉ cần mở OBS, hoặc chĩa điện thoại vào màn hình — vẫn ghi
// được mà không giương cờ nào. Không có kiến trúc nào chặn được điều đó: khung
// hình đã nằm trên máy họ thì nó thuộc về họ.
//
// Cờ này vẫn đáng có, vì nó phục vụ đúng cái nó làm được: cho người dùng NGAY
// THẲNG một cách nói ra điều họ đang làm, và cho cả phòng biết. Nó là chuẩn mực
// xã hội được cài vào giao diện, không phải một cái khoá — và trình bày nó như
// một cái khoá mới là chỗ nguy hiểm, vì khi đó người ta tin vào một sự im lặng
// không chứng minh được điều gì.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RoomEvent, type Participant, type Room } from 'livekit-client'
import { isCustom } from './emoji'

export type ChatMessage = {
  id: string
  from: string
  text: string
  at: number
  mine: boolean
}

export type Reaction = {
  id: string
  from: string
  /** TÊN emoji riêng của Z-Meet — xem src/emoji.tsx, không phải ký tự Unicode. */
  emoji: string
  at: number
}

/**
 * Said là những gì một người NÓI ra và hiện lên như một dòng của cuộc trò
 * chuyện. Tách khỏi Wire vì `rec` không phải vậy: nó là trạng thái, không phải
 * lời nói, và nó không có chỗ trong ô chat cũng như trong danh sách biểu cảm.
 */
type Said = { t: 'chat'; text: string } | { t: 'reaction'; emoji: string }

type Wire =
  | Said
  // Cờ "tôi đang ghi hình về máy tôi" — xem mục ghi hình cục bộ phía trên.
  | { t: 'rec'; on: boolean }

const MAX_CHAT = 500
const MAX_TEXT = 2000
const REACTION_TTL = 4000

/**
 * Nhịp loan báo lại cờ ghi hình cục bộ.
 *
 * Kênh dữ liệu KHÔNG có lịch sử: gói gửi trước khi một người vào phòng thì với
 * họ nó chưa từng tồn tại. Một nhịp lặp lại giải quyết cả ba trường hợp — người
 * vào sau, gói bị rơi, và client vừa nối lại — bằng cùng một cơ chế, thay vì ba
 * đoạn mã phải nhớ gọi nhau. Gói nặng chừng hai chục byte.
 */
const REC_BEAT_MS = 5000

/**
 * sanitize cắt và làm sạch văn bản đến từ NGƯỜI KHÁC.
 *
 * ⚠️ Payload trên kênh dữ liệu đến thẳng từ một client khác — LiveKit chỉ chuyển
 * tiếp, không kiểm nội dung. Coi nó là dữ liệu đáng tin là mở đúng cửa mà một
 * người trong phòng dùng SDK trực tiếp sẽ đi qua.
 *
 * React đã escape khi render, nên đây là về ĐỘ DÀI và ký tự điều khiển: một tin
 * nhắn 5MB hoặc đầy ký tự đảo chiều hai byte làm hỏng bố cục của mọi người.
 */
function sanitize(s: unknown): string {
  if (typeof s !== 'string') return ''
  return (
    s
      .slice(0, MAX_TEXT)
      // Bỏ ký tự điều khiển, giữ lại xuống dòng và tab.
      .replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, '')
      // Bỏ ký tự đổi chiều văn bản của Unicode. Chúng vô hình và đảo được thứ
      // tự hiển thị của cả dòng — đủ để một tin nhắn hiện ra thành nội dung
      // khác hẳn thứ người gửi thật sự gõ.
      .replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '')
      .trim()
  )
}

/**
 * useRoomData — chat, biểu cảm, và cờ ghi hình cục bộ của cả phòng.
 *
 * `localRecording` là trạng thái máy ghi của CHÍNH MÌNH (xem recorder.ts). Hook
 * này loan báo nó ra phòng và gom cờ của người khác về, để mọi người nhìn thấy
 * ai đang ghi.
 */
export function useRoomData(room: Room | null, myName: string, localRecording = false) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [reactions, setReactions] = useState<Reaction[]>([])
  const [unread, setUnread] = useState(0)
  // identity → tên hiển thị của những người khác đang ghi hình cục bộ.
  const [others, setOthers] = useState<Map<string, string>>(() => new Map())
  const seq = useRef(0)

  const push = useCallback((from: string, w: Said, mine: boolean) => {
    const id = `${Date.now()}-${seq.current++}`
    if (w.t === 'chat') {
      const text = sanitize(w.text)
      if (!text) return
      setMessages((prev) => [...prev, { id, from, text, at: Date.now(), mine }].slice(-MAX_CHAT))
      if (!mine) setUnread((n) => n + 1)
      return
    }
    // Reaction chỉ nhận TÊN trong danh mục đóng. Cho gửi chuỗi tuỳ ý nghĩa là
    // cho phép hiện một thứ bất kỳ nổi trên màn hình của mọi người — một kênh
    // chat thứ hai không ai kiểm duyệt được, và nó luôn bị lạm dụng.
    if (typeof w.emoji !== 'string' || !isCustom(w.emoji)) return
    const r: Reaction = { id, from, emoji: w.emoji, at: Date.now() }
    setReactions((prev) => [...prev, r])
    setTimeout(() => setReactions((prev) => prev.filter((x) => x.id !== r.id)), REACTION_TTL)
  }, [])

  const publish = useCallback(
    async (w: Wire) => {
      if (!room) return
      const data = new TextEncoder().encode(JSON.stringify(w))
      // Chat dùng reliable: mất một tin nhắn là mất thông tin. Reaction thì
      // không — nó là thứ phù du, và một reaction đến muộn ba giây còn tệ hơn
      // một reaction bị mất. Cờ ghi hình cũng reliable: nó là một lời cảnh báo
      // về quyền riêng tư, và một gói rơi ở đây là một người không biết mình
      // đang bị ghi.
      await room.localParticipant.publishData(data, { reliable: w.t !== 'reaction' })
    },
    [room],
  )

  useEffect(() => {
    if (!room) return
    const onData = (payload: Uint8Array, participant?: Participant) => {
      let w: Wire
      try {
        w = JSON.parse(new TextDecoder().decode(payload)) as Wire
      } catch {
        return // payload hỏng không được làm sập giao diện của người nhận
      }

      if (w?.t === 'rec') {
        // identity là thứ SFU gắn vào gói, không phải thứ người gửi tự khai —
        // nên không ai giương cờ ghi hình hộ người khác được.
        const id = participant?.identity
        if (!id) return
        setOthers((prev) => {
          const next = new Map(prev)
          if (w.on === true) next.set(id, participant.name || id)
          else next.delete(id)
          return next
        })
        return
      }

      if (w?.t !== 'chat' && w?.t !== 'reaction') return
      push(participant?.name || participant?.identity || '?', w, false)
    }

    // Người rời phòng thì cờ của họ đi theo. Không dọn ở đây thì một người
    // thoát giữa lúc đang ghi để lại cảnh báo đỏ treo mãi trên màn hình của
    // những người còn lại.
    const onGone = (p: Participant) => {
      setOthers((prev) => {
        if (!prev.has(p.identity)) return prev
        const next = new Map(prev)
        next.delete(p.identity)
        return next
      })
    }

    room.on(RoomEvent.DataReceived, onData).on(RoomEvent.ParticipantDisconnected, onGone)
    return () => {
      room.off(RoomEvent.DataReceived, onData).off(RoomEvent.ParticipantDisconnected, onGone)
    }
  }, [room, push])

  // Loan báo cờ ghi hình của chính mình: ngay khi nó đổi, mỗi REC_BEAT_MS một
  // lần trong lúc đang ghi, và thêm một lần khi có người vừa vào phòng — để họ
  // không phải chờ hết một nhịp mới biết mình đang bị ghi.
  useEffect(() => {
    if (!room) return
    const announce = () => void publish({ t: 'rec', on: localRecording }).catch(() => {})
    announce()
    if (!localRecording) return

    const onJoin = () => announce()
    room.on(RoomEvent.ParticipantConnected, onJoin)
    const beat = setInterval(announce, REC_BEAT_MS)
    return () => {
      clearInterval(beat)
      room.off(RoomEvent.ParticipantConnected, onJoin)
    }
  }, [room, publish, localRecording])

  const send = useCallback(
    async (w: Said) => {
      await publish(w)
      push(myName, w, true)
    },
    [publish, myName, push],
  )

  // Mình nằm chung một danh sách với mọi người: cùng một banner, cùng một dấu
  // trên ô. Người đang ghi cũng cần thấy máy ghi của họ thật sự đang chạy.
  const recorders = useMemo(() => {
    const all = new Map(others)
    const me = room?.localParticipant.identity
    if (localRecording && me) all.set(me, myName)
    return all
  }, [others, localRecording, room, myName])

  return {
    messages,
    reactions,
    unread,
    /** identity → tên hiển thị của mọi người đang ghi hình cục bộ, kể cả mình. */
    recorders,
    clearUnread: () => setUnread(0),
    sendChat: (text: string) => send({ t: 'chat', text }),
    sendReaction: (emoji: string) => send({ t: 'reaction', emoji }),
  }
}
